import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import { Box, Button, IconButton, Modal, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material'
import { fetchCollateral, saveCollateral } from '@react/fetchers/collateralFetchers'
import { useOtChainState } from '@react/redux/store'
import { UnderlyingCollateralCTO } from '@shared/UnderlyingCollateralCTO'
import React, { useEffect, useState } from 'react'
import UnderlyingCollateralForm from './UnderlyingCollateralForm'
import AddIcon from '@mui/icons-material/Add'

type Props = {
  poolAddr: string
}

export default function UnderlyingCollateralView({ poolAddr }: Props) {
  const {
    stageConfiguration: { restAPIKey, serverApiBaseUrl },
    cognitoUser,
  } = useOtChainState()
  const [rows, setRows] = useState<UnderlyingCollateralCTO[]>([])
  const [editIndex, setEditIndex] = useState<number | null>(null)
  const [isAdding, setIsAdding] = useState(false)

  // Fetch collateral data from backend
  const fetchAndSetCollateral = () => {
    if (poolAddr && serverApiBaseUrl && restAPIKey) {
      fetchCollateral(serverApiBaseUrl, restAPIKey, poolAddr)
        .then(setRows)
        .catch((err) => {
          console.error('Error fetching collateral:', err)
        })
    }
  }

  useEffect(() => {
    fetchAndSetCollateral()
  }, [poolAddr, serverApiBaseUrl, restAPIKey])

  const handleEdit = (idx: number) => setEditIndex(idx)
  const handleDelete = (idx: number) => {
    const newRows = rows.filter((_, i) => i !== idx)
    saveCollateralArray(newRows)
  }
  const handleAdd = () => setIsAdding(true)

  const handleSave = (item: UnderlyingCollateralCTO, idx: number | null) => {
    let newRows: UnderlyingCollateralCTO[]
    if (idx === null) {
      // Add
      newRows = [...rows, item]
    } else {
      // Edit
      newRows = rows.map((row, i) => (i === idx ? item : row))
    }
    saveCollateralArray(newRows)
    setIsAdding(false)
    setEditIndex(null)
  }

  const saveCollateralArray = async (collateralArray: UnderlyingCollateralCTO[]) => {
    try {
      await saveCollateral(serverApiBaseUrl, poolAddr, collateralArray, restAPIKey, cognitoUser)
      setRows(collateralArray) // Update local state
    } catch (err) {
      console.error('Failed to save collateral array:', err)
      // Optionally show an error to the user
    }
  }

  const modalBoxSx = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 600,
    bgcolor: 'background.paper',
    boxShadow: 24,
    borderRadius: 2,
    p: 4,
  }

  return (
    <>
      <Button startIcon={<AddIcon />} onClick={handleAdd}>
        Add Collateral
      </Button>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Asset Type</TableCell>
            <TableCell>Asset ID</TableCell>
            <TableCell>Yield/Coupon</TableCell>
            <TableCell>Maturity Date</TableCell>
            <TableCell>Market Value</TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, idx) => (
            <TableRow key={idx}>
              <TableCell>{row.assetType}</TableCell>
              <TableCell>{row.assetId}</TableCell>
              <TableCell>{typeof row.yieldOrCoupon === 'number' ? `${row.yieldOrCoupon}%` : 'N/A'}</TableCell>
              <TableCell>{row.maturityDate && row.maturityDate.trim() !== '' ? row.maturityDate : 'N/A'}</TableCell>
              <TableCell>{Number(row.marketValue).toLocaleString()}</TableCell>
              <TableCell>
                <IconButton onClick={() => handleEdit(idx)}>
                  <EditIcon />
                </IconButton>
                <IconButton onClick={() => handleDelete(idx)}>
                  <DeleteIcon />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {/* Edit Modal */}
      <Modal open={editIndex !== null} onClose={() => setEditIndex(null)}>
        <Box sx={modalBoxSx}>
          {editIndex !== null && (
            <UnderlyingCollateralForm
              collateral={rows[editIndex]}
              index={editIndex}
              onSave={handleSave}
              onClose={() => setEditIndex(null)}
              poolAddr={poolAddr}
            />
          )}
        </Box>
      </Modal>

      {/* Add Modal */}
      <Modal open={isAdding} onClose={() => setIsAdding(false)}>
        <Box sx={modalBoxSx}>
          <UnderlyingCollateralForm
            collateral={{ assetType: '', assetId: '', maturityDate: '', marketValue: 0, yieldOrCoupon: 0 }}
            index={null}
            onSave={handleSave}
            onClose={() => setIsAdding(false)}
            poolAddr={poolAddr}
          />
        </Box>
      </Modal>
    </>
  )
}
